// frontend/src/pages/AgriServis.jsx
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import {
  FaFilter,
  FaRedo,
  FaMapMarkerAlt,
  FaTimes,
  FaWhatsapp,
  FaFacebookF,
  FaInstagram,
  FaTiktok,
} from "react-icons/fa";
import "leaflet/dist/leaflet.css";
import "./AgriServis.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const ENV_API = process.env.REACT_APP_API_URL || "/api";
const CURRENT_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const IS_LOCAL_WEB =
  typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
const API = (() => {
  const raw = String(ENV_API || "").trim();
  if (!/^https?:\/\//i.test(raw)) return raw || "/api";
  try {
    const u = new URL(raw);
    const isLocalApi = /^(localhost|127\.0\.0\.1)$/i.test(u.hostname);
    if (!IS_LOCAL_WEB && isLocalApi) return `${CURRENT_ORIGIN}/api`;
    if (typeof window !== "undefined" && window.location.protocol === "https:" && u.protocol === "http:") {
      return raw.replace(/^http:/i, "https:");
    }
    return raw;
  } catch {
    return raw || "/api";
  }
})();
const API_ORIGIN = API.startsWith("http")
  ? API.replace(/\/api\/?$/, "")
  : (CURRENT_ORIGIN || "");
const DEFAULT_MAP_CENTER = [-2.5, 115.0];
const DEFAULT_MAP_ZOOM = 5;

/* ===== Helper ===== */
const isPoint = (o) => Number.isFinite(o?.lat) && Number.isFinite(o?.lng);
const onlyValid = (arr = []) => arr.filter(isPoint);
const normDigits = (v) => String(v || "").replace(/\D/g, "");
const toIndonesiaMsisdn = (v) => {
  const d = normDigits(v);
  if (!d) return "";
  if (d.startsWith("62")) return d;
  if (d.startsWith("0")) return `62${d.slice(1)}`;
  return `62${d}`;
};
const toDisplayPhone = (v) => {
  const d = toIndonesiaMsisdn(v);
  return d ? `+${d}` : "";
};
const formatRupiah = (v) => {
  if (v === null || v === undefined || v === "" || isNaN(v)) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `Rp ${n.toLocaleString("id-ID")}`;
};
const initialsOf = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "SK";

const resolvePhotoUrl = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return null;
  if (raw.startsWith("blob:")) return null;
  if (raw.startsWith("data:")) return raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      if (u.pathname.startsWith("/uploads/")) {
        return `${API}${u.pathname}`;
      }
      if (u.pathname.startsWith("/api/uploads/")) {
        return `${API}${u.pathname.replace(/^\/api/, "")}`;
      }
    } catch {
      return raw;
    }
    return raw;
  }
  if (raw.startsWith("/uploads/")) return `${API}${raw}`;
  if (raw.startsWith("/api/uploads/")) return `${API}${raw.replace(/^\/api/, "")}`;
  if (raw.startsWith("/")) return API_ORIGIN ? `${API_ORIGIN}${raw}` : raw;
  return API_ORIGIN ? `${API_ORIGIN}/${raw}` : raw;
};

const normalizeExternalUrl = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

function parseOptionsJson(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
  } catch {
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
}

function serviceDisplayName(service) {
  if (service?.category === "Bibit Kakao") return "Bibit Kakao";
  const title = String(service?.title || "").trim();
  const category = String(service?.category || "").trim();
  if (category === "Jasa" && title) return /^jasa\s/i.test(title) ? title : `Jasa ${title}`;
  if (category === "Bibit Buah dan Kayu" && title) {
    return /^bibit\s/i.test(title) ? title : `Bibit ${title}`;
  }
  if (title) return title;
  return service?.category || service?.title || "Layanan / Produk";
}

const getServiceKey = (service, index = 0) => String(service?.id ?? index);

function buildProductOrderLink(provider, service, index = 0) {
  if (typeof window === "undefined" || !provider?.id) return "";
  const url = new URL("/agriservis", window.location.origin);
  url.searchParams.set("provider", String(provider.id));
  url.searchParams.set("product", getServiceKey(service, index));
  url.searchParams.set("openOrder", "1");
  return url.toString();
}

async function copyText(value) {
  if (!value) return false;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }
  const input = document.createElement("textarea");
  input.value = value;
  input.setAttribute("readonly", "");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(input);
  return ok;
}

function ImageWithFallback({
  src,
  alt,
  className = "",
  fallbackClassName = "as-item-noimg",
  fallback = "No Photo",
  loading = "lazy",
}) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return <span className={fallbackClassName}>{fallback}</span>;
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      onError={() => setFailed(true)}
    />
  );
}

function ProviderModalHeader({ provider }) {
  const socialLinks = [
    provider?.phone
      ? {
          href: `https://wa.me/${toIndonesiaMsisdn(provider.phone)}`,
          className: "wa",
          title: "WhatsApp",
          icon: <FaWhatsapp />,
        }
      : null,
    provider?.facebook_url
      ? {
          href: provider.facebook_url,
          className: "fb",
          title: "Facebook",
          icon: <FaFacebookF />,
        }
      : null,
    provider?.instagram_url
      ? {
          href: provider.instagram_url,
          className: "ig",
          title: "Instagram",
          icon: <FaInstagram />,
        }
      : null,
    provider?.tiktok_url
      ? {
          href: provider.tiktok_url,
          className: "tk",
          title: "TikTok",
          icon: <FaTiktok />,
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="as-provider as-provider--modal">
      <div className="as-provider-identity">
        <div className="as-provider-name">{provider?.name || "Sahabat Kakao"}</div>
        <div className="as-provider-sub">
          {provider?.kecamatan}, {provider?.kabupaten}
        </div>
      </div>
      {socialLinks.length > 0 && (
        <div className="as-modal-social-links">
          {socialLinks.map((link) => (
            <a
              key={link.title}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              className={`as-contact-link ${link.className}`}
              title={link.title}
              aria-label={link.title}
            >
              {link.icon}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceListModal({
  open,
  onClose,
  provider,
  services = [],
  onOrder,
  onCopyLink,
  copiedServiceKey,
}) {
  if (!open) return null;

  return (
    <div className="as-modal-backdrop" onClick={onClose}>
      <div className="as-modal compact as-service-modal" onClick={(e) => e.stopPropagation()}>
        <div className="as-modal-header">
          <h3 className="as-modal-title">Layanan / Produk Sahabat Kakao</h3>
          <button className="as-close" onClick={onClose} aria-label="Tutup">
            <FaTimes aria-hidden="true" />
          </button>
        </div>
        <div className="as-modal-body">
          <ProviderModalHeader provider={provider} />

          <div className="as-info-services as-info-services--modal">
            {services.length > 0 ? (
              services.map((s, idx) => {
                const serviceKey = getServiceKey(s, idx);
                return (
                  <div className="as-info-item" key={`${s.id || s.title}-${idx}`}>
                    <div className="as-item-thumb">
                      <ImageWithFallback src={s.photo_url} alt={serviceDisplayName(s)} />
                    </div>
                    <div className="as-item-meta">
                      <div className="as-item-title">{serviceDisplayName(s)}</div>
                      <div className="as-item-price">
                        <span className="price-label">Harga</span>
                        {formatRupiah(s.unit_price)}
                      </div>
                      <div className="as-item-actions as-item-actions--modal">
                        <button
                          type="button"
                          className="btn btn-copy sm"
                          onClick={() => onCopyLink?.(provider, s, idx)}
                          title="Copy link produk"
                        >
                          {copiedServiceKey === `${provider?.id}-${serviceKey}` ? "Copied" : "Copy link"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-order sm"
                          onClick={() => onOrder?.(s, idx)}
                          title="Order produk ini"
                        >
                          Order
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="as-empty">Belum ada layanan/produk.</p>
            )}
          </div>

          {provider && (
            <div className="as-actions compact-row">
              <button type="button" className="as-btn ghost" onClick={onClose}>
                Tutup
              </button>
              <button type="button" className="as-btn primary" onClick={() => onOrder?.()}>
                Order Sekarang
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== Modal Order (ringkas) ===== */
function OrderModal({
  open,
  onClose,
  provider,
  services,
  currentUserId = null,
  selectedServiceId = "",
}) {
  const [serviceKey, setServiceKey] = useState("");
  const [variantQty, setVariantQty] = useState({});
  const [qty, setQty] = useState(1);
  const [farmerId, setFarmerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [buyerGender, setBuyerGender] = useState("");
  const [phone, setPhone] = useState("");
  const [desa, setDesa] = useState("");
  const [kecamatan, setKecamatan] = useState("");
  const [kabupaten, setKabupaten] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const requestedKey = String(selectedServiceId || "");
      const hasRequested = (services || []).some(
        (service, index) => getServiceKey(service, index) === requestedKey
      );
      setServiceKey(
        hasRequested
          ? requestedKey
          : services?.length
          ? getServiceKey(services[0], 0)
          : ""
      );
      setVariantQty({});
      setQty(1);
      setFarmerId("");
      setCustomerName("");
      setBuyerGender("");
      setPhone("");
      setDesa("");
      setKecamatan("");
      setKabupaten("");
      setCoupon(null);
      setCouponLoading(false);
    }
  }, [open, services, selectedServiceId]);

  // ✅ Hooks TIDAK BOLEH dipanggil setelah return conditional.
  // Maka semua useMemo dipanggil dulu, baru return null di bawah.

  const selectedService = React.useMemo(() => {
    const selectedId = String(serviceKey || "");
    return (
      (services || []).find(
        (s, index) => getServiceKey(s, index) === selectedId
      ) || null
    );
  }, [serviceKey, services]);

  const serviceOptions = React.useMemo(
    () => parseOptionsJson(selectedService?.options || selectedService?.options_json),
    [selectedService]
  );

  const selectedVariantRows = React.useMemo(
    () =>
      serviceOptions
        .map((name) => ({
          name,
          quantity: Math.max(0, parseInt(variantQty[name], 10) || 0),
        }))
        .filter((item) => item.quantity > 0),
    [serviceOptions, variantQty]
  );

  const unitPrice = React.useMemo(() => {
    const v = selectedService?.unit_price;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  }, [selectedService]);

  const qtyNum = React.useMemo(() => {
    if (serviceOptions.length > 0) {
      const total = selectedVariantRows.reduce((sum, item) => sum + item.quantity, 0);
      return total > 0 ? total : 0;
    }
    const n = parseInt(qty, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }, [qty, selectedVariantRows, serviceOptions.length]);

  const totalPrice = React.useMemo(() => {
    if (unitPrice === null) return null;
    return unitPrice * qtyNum;
  }, [unitPrice, qtyNum]);

  const couponBalance = React.useMemo(() => {
    if (!coupon) return 0;
    return Math.max(0, Number(coupon.coupon_balance ?? coupon.coupon_value) || 0);
  }, [coupon]);

  const couponDiscount = React.useMemo(() => {
    if (!coupon || totalPrice === null) return 0;
    return Math.min(couponBalance, Math.max(0, totalPrice));
  }, [coupon, couponBalance, totalPrice]);

  const couponRemaining = React.useMemo(
    () => Math.max(0, couponBalance - couponDiscount),
    [couponBalance, couponDiscount]
  );

  const finalPrice = React.useMemo(() => {
    if (totalPrice === null) return null;
    return Math.max(0, totalPrice - couponDiscount);
  }, [couponDiscount, totalPrice]);

  useEffect(() => {
    if (!open) return;
    setVariantQty((current) => {
      const next = {};
      for (const item of serviceOptions) {
        if (current[item]) next[item] = current[item];
      }
      return next;
    });
  }, [open, serviceKey, serviceOptions]);

  // ✅ return null dipindah ke sini (setelah semua hooks)
  if (!open) return null;

  const generateCoupon = async () => {
    const normalizedFarmerId = farmerId.trim();
    if (!normalizedFarmerId) {
      alert("Masukkan ID Petani terlebih dahulu.");
      return;
    }

    setCouponLoading(true);
    try {
      const { data } = await axios.post(`${API}/coupons/generate`, {
        farmer_id: normalizedFarmerId,
      });
      if (!data?.success || !data?.coupon) throw new Error(data?.message || "Gagal membuat kupon.");
      const generated = data.coupon;
      setCoupon(generated);
      setCustomerName((current) => current || generated.farmer_name || "");
      setDesa((current) => current || generated.village || "");
      setKecamatan((current) => current || generated.district || "");
      setKabupaten((current) => current || generated.regency || "");
    } catch (error) {
      setCoupon(null);
      const message = error?.response?.data?.message || error.message || "Gagal membuat kupon.";
      alert(message === "Petani tidak memiliki kupon" ? "Petani tidak memiliki kupon" : message);
    } finally {
      setCouponLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const selectedTitle = serviceDisplayName(selectedService);
      const variantText = selectedVariantRows.length
        ? `Varietas/opsi:\n${selectedVariantRows
            .map((item) => `- ${item.name}: ${item.quantity} pohon`)
            .join("\n")}`
        : "";

      const payload = {
        provider_user_id: provider?.id,
        service_id: Number(selectedService?.id) || null,
        farmer_id: farmerId.trim(),
        buyer_name: customerName.trim(),
        buyer_gender: buyerGender,
        buyer_phone: toIndonesiaMsisdn(phone),
        buyer_desa: (desa || "").trim(),
        buyer_kecamatan: (kecamatan || "").trim(),
        buyer_kabupaten: (kabupaten || "").trim(),
        category: (selectedService?.category || "").trim(),
        title: selectedTitle.trim(),
        quantity: serviceOptions.length > 0 ? qtyNum : Math.max(1, parseInt(qty, 10) || 1),
        subtotal: totalPrice,
        coupon_code: coupon?.coupon_code || null,
        notes: variantText,
        provider_phone: toIndonesiaMsisdn(provider?.phone || provider?.whatsapp || ""),
      };

      if (
        !payload.farmer_id ||
        !payload.buyer_name ||
        !payload.buyer_gender ||
        !payload.buyer_phone
      ) {
        alert("ID Petani, nama, jenis kelamin, dan No. HP wajib diisi.");
        return;
      }
      if (!selectedService || !payload.category || !payload.title) {
        alert("Silakan pilih jenis layanan/produk.");
        return;
      }
      if (serviceOptions.length > 0 && selectedVariantRows.length === 0) {
        alert("Pilih minimal satu varietas dan isi jumlah pohonnya.");
        return;
      }
      if (!payload.provider_phone) {
        alert("Nomor WhatsApp penyedia servis tidak tersedia.");
        return;
      }

      const endpoints = [`${API}/agriservis/order`, `${API}/business/orders`];
      let lastErr = null;
      for (const url of endpoints) {
        try {
          const response = await axios.post(url, payload, {
            headers: { "Content-Type": "application/json" },
          });
          alert(response?.data?.message || "Order berhasil dibuat");
          onClose?.();
          return;
        } catch (err) {
          const status = err?.response?.status;
          if (status === 404) {
            lastErr = err;
            continue;
          }
          throw err;
        }
      }
      throw lastErr || new Error("Semua endpoint order gagal.");
    } catch (err) {
      console.error("order error:", err);
      const msg =
        err?.response?.data?.message ||
        (err?.response?.status === 404
          ? "Endpoint order tidak ditemukan di server."
          : null) ||
        "Gagal mengirim order.";
      alert(msg);
    }
  };

  return (
    <div className="as-modal-backdrop" onClick={onClose}>
      <div className="as-modal compact as-order-modal" onClick={(e) => e.stopPropagation()}>
        <div className="as-modal-header">
          <h3 className="as-modal-title">Order Layanan</h3>
          <button className="as-close" onClick={onClose} aria-label="Tutup">
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        <div className="as-modal-body">
          <ProviderModalHeader provider={provider} />

          <form onSubmit={submit} className="as-form dense">
            <label>
              <span className="as-label">Jenis Layanan / Produk</span>
              <select
                value={serviceKey}
                onChange={(e) => setServiceKey(e.target.value)}
                required
              >
                {services?.length ? (
                  services.map((s, i) => (
                    <option
                      key={`${s.id || i}-${s.category}-${i}`}
                      value={getServiceKey(s, i)}
                    >
                      {serviceDisplayName(s)}
                    </option>
                  ))
                ) : (
                  <option value="">(Belum ada layanan)</option>
                )}
              </select>
            </label>

            {serviceOptions.length > 0 && (
              <div className="as-variant-box">
                <div className="as-variant-head">
                  <span className="as-label">
                    {selectedService?.category === "Bibit Kakao" ? "Varietas Bibit" : "Pilihan"}
                  </span>
                  <span>Isi jumlah pohon per varietas</span>
                </div>
                <div className="as-variant-list">
                  {serviceOptions.map((item) => {
                    const checked = Object.prototype.hasOwnProperty.call(variantQty, item);
                    return (
                      <div className="as-variant-row" key={item}>
                        <label className="as-variant-check">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) =>
                              setVariantQty((prev) => {
                                const next = { ...prev };
                                if (e.target.checked) next[item] = next[item] || 1;
                                else delete next[item];
                                return next;
                              })
                            }
                          />
                          <span>{item}</span>
                        </label>
                        <input
                          className="as-variant-input"
                          type="number"
                          min={1}
                          value={checked ? variantQty[item] || 1 : ""}
                          onChange={(e) =>
                            setVariantQty((prev) => ({
                              ...prev,
                              [item]: e.target.value,
                            }))
                          }
                          disabled={!checked}
                          placeholder="0"
                          aria-label={`Jumlah pohon ${item}`}
                        />
                        <span className="as-variant-unit">pohon</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ✅ Baris sejajar: Jumlah | Harga | Total */}
            <div className="as-order-row3">
              <label className="as-order-field">
                <span className="as-label">Jumlah Order</span>
                <input
                  type="number"
                  min={1}
                  value={serviceOptions.length > 0 ? qtyNum || "" : qty}
                  onChange={(e) => setQty(e.target.value)}
                  readOnly={serviceOptions.length > 0}
                  aria-readonly={serviceOptions.length > 0 ? "true" : undefined}
                  required={serviceOptions.length === 0}
                />
              </label>

              <label className="as-order-field">
                <span className="as-label">Harga</span>
                <input
                  type="text"
                  value={formatRupiah(unitPrice)}
                  readOnly
                  aria-readonly="true"
                />
              </label>

              <label className="as-order-field">
                <span className="as-label">Total Harga</span>
                <input
                  type="text"
                  value={formatRupiah(totalPrice)}
                  readOnly
                  aria-readonly="true"
                />
              </label>
            </div>

            <div className="as-farmer-row">
              <label className="as-farmer-field">
                <span className="as-label">ID Petani</span>
                <div className="as-farmer-id-control">
                  <input
                    type="text"
                    value={farmerId}
                    onChange={(e) => {
                      setFarmerId(e.target.value);
                      if (coupon && e.target.value.trim() !== coupon.farmer_id) setCoupon(null);
                    }}
                    maxLength={100}
                    placeholder="Masukkan ID petani"
                    autoComplete="off"
                    required
                  />
                  <button
                    type="button"
                    className="as-coupon-generate"
                    onClick={generateCoupon}
                    disabled={couponLoading || !farmerId.trim()}
                  >
                    {couponLoading ? "Memeriksa..." : "Generate Kupon"}
                  </button>
                </div>
              </label>
            </div>

            <div className="as-buyer-row">
              <label>
                <span className="as-label">Nama Pengorder/Petani</span>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Masukkan nama lengkap"
                  autoComplete="name"
                  required
                />
              </label>

              <label>
                <span className="as-label">Jenis Kelamin</span>
                <select
                  value={buyerGender}
                  onChange={(e) => setBuyerGender(e.target.value)}
                  required
                >
                  <option value="">Pilih</option>
                  <option value="Laki-laki">Laki-laki</option>
                  <option value="Perempuan">Perempuan</option>
                </select>
              </label>
            </div>

            {coupon && (
              <div className="as-coupon-result" role="status">
                <div>
                  <span>Kupon siap digunakan</span>
                  <strong>{coupon.coupon_code}</strong>
                  <small>{coupon.farmer_name}{coupon.institution ? ` - ${coupon.institution}` : ""}</small>
                </div>
                <div className="as-coupon-values">
                  <span>Saldo tersedia <strong>{formatRupiah(couponBalance)}</strong></span>
                  <span>Dipakai order ini <strong>- {formatRupiah(couponDiscount)}</strong></span>
                  <span>Sisa saldo <strong>{formatRupiah(couponRemaining)}</strong></span>
                  {finalPrice !== null && <span className="as-coupon-total">Total Bayar <strong>{formatRupiah(finalPrice)}</strong></span>}
                </div>
              </div>
            )}

            <label>
              <span className="as-label">No HP / WhatsApp</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="62xxxxxxxxxx"
              />
            </label>

            <div className="as-grid-3">
              <label>
                <span className="as-label">Desa</span>
                <input
                  type="text"
                  value={desa}
                  onChange={(e) => setDesa(e.target.value)}
                  required
                />
              </label>
              <label>
                <span className="as-label">Kecamatan</span>
                <input
                  type="text"
                  value={kecamatan}
                  onChange={(e) => setKecamatan(e.target.value)}
                  required
                />
              </label>
              <label>
                <span className="as-label">Kabupaten</span>
                <input
                  type="text"
                  value={kabupaten}
                  onChange={(e) => setKabupaten(e.target.value)}
                  required
                />
              </label>
            </div>

            <div className="as-actions compact-row">
              <button type="button" className="as-btn ghost" onClick={onClose}>
                Batal
              </button>
              <button type="submit" className="as-btn primary">
                Order Sekarang
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* Kendali peta untuk flyTo/fitBounds */
function MapController({ center, zoom, bounds }) {
  const map = useMap();
  React.useEffect(() => {
    if (bounds && bounds.length === 2) {
      map.fitBounds(bounds, { padding: [48, 48] });
      return;
    }
    if (center && Number.isFinite(center[0]) && Number.isFinite(center[1])) {
      map.flyTo(center, typeof zoom === "number" ? zoom : map.getZoom(), {
        duration: 0.8,
      });
    }
  }, [center?.[0], center?.[1], zoom, JSON.stringify(bounds), map]);
  return null;
}

function MapViewportSync({ onBoundsChange }) {
  const map = useMap();

  React.useEffect(() => {
    const emitBounds = () => {
      const b = map.getBounds();
      onBoundsChange?.({
        south: b.getSouth(),
        west: b.getWest(),
        north: b.getNorth(),
        east: b.getEast(),
      });
    };

    emitBounds();
    map.on("moveend", emitBounds);
    map.on("zoomend", emitBounds);
    return () => {
      map.off("moveend", emitBounds);
      map.off("zoomend", emitBounds);
    };
  }, [map, onBoundsChange]);

  return null;
}

/* ===== Halaman ===== */
export default function AgriServis() {
  const [lokasi, setLokasi] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [selectedProv, setSelectedProv] = useState("");
  const [selectedKab, setSelectedKab] = useState("");
  const [selectedKec, setSelectedKec] = useState("");
  const [servicesMap, setServicesMap] = useState({});

  const [orderOpen, setOrderOpen] = useState(false);
  const [orderProvider, setOrderProvider] = useState(null);
  const [orderServices, setOrderServices] = useState([]);
  const [orderServiceId, setOrderServiceId] = useState("");
  const [servicesOpen, setServicesOpen] = useState(false);
  const [serviceModalProvider, setServiceModalProvider] = useState(null);
  const [serviceModalServices, setServiceModalServices] = useState([]);
  const [copiedServiceKey, setCopiedServiceKey] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [currentUserId] = useState(null);
  const deepLinkHandledRef = useRef(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await axios.get(`${API}/agriservis/providers`);
        const users = res.data.users || res.data.data || [];
        const sahabatRaw = users
          .filter((u) => u.latitude !== null && u.longitude !== null)
          .map((u) => ({
            id: u.id,
            name: u.full_name || u.name,
            kecamatan: u.kecamatan || u.sub_district,
            kabupaten: u.kabupaten || u.district,
            provinsi: u.provinsi || u.province,
            email: u.email,
            phone: u.phone || u.whatsapp,
            service: u.service || "Belum ada layanan",
            profile_pic: resolvePhotoUrl(
              u.profile_pic || u.photo_url || u.avatar_url || null
            ),
            facebook_url: normalizeExternalUrl(u.facebook_url || ""),
            instagram_url: normalizeExternalUrl(u.instagram_url || ""),
            tiktok_url: normalizeExternalUrl(u.tiktok_url || ""),
            lat: parseFloat(u.latitude),
            lng: parseFloat(u.longitude),
          }));

        const sahabat = onlyValid(sahabatRaw);
        setLokasi(sahabat);
        setFiltered(sahabat);
      } catch (err) {
        console.error(err);
        alert("Gagal memuat lokasi");
      }
    }
    fetchData();
  }, []);

  const [mapCenter] = useState(DEFAULT_MAP_CENTER);
  const [mapZoom] = useState(DEFAULT_MAP_ZOOM);
  const [flyTo, setFlyTo] = useState(null);
  const [fitBounds, setFitBounds] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);

  const loadServices = async (userId) => {
    if (servicesMap[userId]) return servicesMap[userId];
    try {
      const { data } = await axios.get(`${API}/business`, {
        params: { user_id: userId },
      });
      const list = (data?.data || data?.services || []).map((x) => ({
        id: x.id,
        category: x.category,
        title: x.title,
        options: parseOptionsJson(x.options || x.options_json),
        options_json: x.options_json,
        photo_url: resolvePhotoUrl(x.photo_url || x.photoUrl || null),
        unit_price:
          typeof x.unit_price === "number"
            ? x.unit_price
            : typeof x.unitPrice === "number"
            ? x.unitPrice
            : x.unit_price ?? x.unitPrice ?? null,
      }));
      setServicesMap((m) => ({ ...m, [userId]: list }));
      return list;
    } catch (e) {
      console.error("load services error:", e);
      setServicesMap((m) => ({ ...m, [userId]: [] }));
      return [];
    }
  };

  const handleFilter = () => {
    setOpenId(null);
    setHoveredId(null);

    const outRaw = lokasi.filter(
      (l) =>
        (!selectedProv || l.provinsi === selectedProv) &&
        (!selectedKab || l.kabupaten === selectedKab) &&
        (!selectedKec || l.kecamatan === selectedKec)
    );
    const out = onlyValid(outRaw);
    setFiltered(out);

    if (out.length === 1) {
      const t = out[0];
      setFlyTo({ center: [t.lat, t.lng], zoom: 13 });
      setFitBounds(null);
      requestAnimationFrame(async () => {
        setOpenId(t.id);
        loadServices(t.id).catch(() => {});
      });
    } else if (out.length > 1) {
      const lats = out.map((o) => o.lat);
      const lngs = out.map((o) => o.lng);
      const sw = [Math.min(...lats), Math.min(...lngs)];
      const ne = [Math.max(...lats), Math.max(...lngs)];
      setFitBounds([sw, ne]);
      setFlyTo(null);
      const first = out[0];
      if (first) {
        requestAnimationFrame(async () => {
          setOpenId(first.id);
          loadServices(first.id).catch(() => {});
        });
      }
    } else {
      setFlyTo({ center: DEFAULT_MAP_CENTER, zoom: DEFAULT_MAP_ZOOM });
      setFitBounds(null);
    }
  };

  const handleClear = () => {
    setSelectedProv("");
    setSelectedKab("");
    setSelectedKec("");
    const all = onlyValid(lokasi);
    setFiltered(all);
    setOpenId(null);
    setHoveredId(null);
    setFlyTo({ center: DEFAULT_MAP_CENTER, zoom: DEFAULT_MAP_ZOOM });
    setFitBounds(null);
  };

  const toRad = (v) => (v * Math.PI) / 180;
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const handleNearest = () => {
    if (!navigator.geolocation) {
      alert("Browser tidak mendukung geolokasi.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        const sorted = onlyValid([...lokasi]).sort(
          (a, b) =>
            getDistance(latitude, longitude, a.lat, a.lng) -
            getDistance(latitude, longitude, b.lat, b.lng)
        );
        const top = sorted.slice(0, 3);
        setFiltered(top);

        const nearest = sorted[0];
        setFitBounds(null);
        if (nearest) {
          setFlyTo({ center: [nearest.lat, nearest.lng], zoom: 14 });
          requestAnimationFrame(async () => {
            setOpenId(nearest.id);
            loadServices(nearest.id).catch(() => {});
          });
        } else {
          setOpenId(null);
        }
        setHoveredId(null);
      },
      (err) => {
        console.warn("Geolocation error:", err);
        alert(
          "Gagal memuat lokasi (kode " + err.code + "). Menampilkan default."
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const openOrder = async (prov, service = null, serviceIndex = 0) => {
    const svcs = await loadServices(prov.id);
    const selectedKey = service ? getServiceKey(service, serviceIndex) : "";
    setOrderProvider(prov);
    setOrderServices(svcs);
    setOrderServiceId(selectedKey);
    setOrderOpen(true);
  };

  const copyProductLink = async (provider, service, serviceIndex = 0) => {
    const link = buildProductOrderLink(provider, service, serviceIndex);
    try {
      const ok = await copyText(link);
      if (!ok) throw new Error("Copy tidak didukung browser.");
      const serviceKey = getServiceKey(service, serviceIndex);
      setCopiedServiceKey(`${provider?.id}-${serviceKey}`);
      window.setTimeout(() => setCopiedServiceKey(""), 1800);
    } catch (err) {
      console.error("copy product link error:", err);
      alert("Gagal menyalin link produk.");
    }
  };

  const openServicesModal = async (prov) => {
    if (!prov) return;
    setOpenId(prov.id);
    setFlyTo({ center: [prov.lat, prov.lng], zoom: 14 });
    const svcs = await loadServices(prov.id);
    setServiceModalProvider(prov);
    setServiceModalServices(svcs || []);
    setServicesOpen(true);
  };

  const activeProvider = React.useMemo(() => {
    const activeId = openId || hoveredId;
    if (activeId) return filtered.find((item) => item.id === activeId) || null;
    return filtered[0] || null;
  }, [filtered, openId, hoveredId]);

  useEffect(() => {
    if (!activeProvider) return;
    if (servicesMap[activeProvider.id]) return;
    loadServices(activeProvider.id).catch(() => {});
  }, [activeProvider, servicesMap]);

  const activeServices = activeProvider ? servicesMap[activeProvider.id] || [] : [];

  useEffect(() => {
    if (deepLinkHandledRef.current || lokasi.length === 0) return;
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const providerId = params.get("provider");
    const productId = params.get("product");
    const shouldOpenOrder = params.get("openOrder") === "1";
    if (!providerId) return;

    const provider = lokasi.find((item) => String(item.id) === String(providerId));
    if (!provider) return;

    deepLinkHandledRef.current = true;
    setFiltered((current) =>
      current.some((item) => item.id === provider.id) ? current : [provider]
    );
    setSelectedProv(provider.provinsi || "");
    setSelectedKab(provider.kabupaten || "");
    setSelectedKec(provider.kecamatan || "");
    setOpenId(provider.id);
    setHoveredId(null);
    setFitBounds(null);
    setFlyTo({ center: [provider.lat, provider.lng], zoom: 14 });

    requestAnimationFrame(async () => {
      const svcs = await loadServices(provider.id);
      const selectedIndex = svcs.findIndex(
        (service, index) => getServiceKey(service, index) === String(productId || "")
      );
      const selectedService = selectedIndex >= 0 ? svcs[selectedIndex] : null;
      if (shouldOpenOrder) {
        setOrderProvider(provider);
        setOrderServices(svcs);
        setOrderServiceId(
          selectedService ? getServiceKey(selectedService, selectedIndex) : ""
        );
        setOrderOpen(true);
      } else {
        setServiceModalProvider(provider);
        setServiceModalServices(svcs || []);
        setServicesOpen(true);
      }
    });
  }, [lokasi]);

  const providersInViewSorted = React.useMemo(() => {
    const ref = userLocation || { lat: DEFAULT_MAP_CENTER[0], lng: DEFAULT_MAP_CENTER[1] };
    return onlyValid(filtered)
      .filter((p) => {
        if (!mapBounds) return true;
        return (
          p.lat >= mapBounds.south &&
          p.lat <= mapBounds.north &&
          p.lng >= mapBounds.west &&
          p.lng <= mapBounds.east
        );
      })
      .map((p) => ({
        ...p,
        distanceKm: getDistance(ref.lat, ref.lng, p.lat, p.lng),
      }))
      .sort((a, b) => a.distanceKm - b.distanceKm);
  }, [filtered, userLocation, mapBounds]);

  return (
    <div className="agriservis-container">
      {/* Toolbar sticky */}
      <div className="as-toolbar-sticky">
        <div className="as-title">
          <FaMapMarkerAlt />
          <span>Temukan Layanan Sahabat Kakao</span>
        </div>

        <div className="as-filters">
          <FaFilter className="icon" />
          <button className="as-btn" onClick={() => handleNearest()}>
            Cari Lokasi Terdekat
          </button>

          <select
            value={selectedProv}
            onChange={(e) => setSelectedProv(e.target.value)}
          >
            <option value="">Provinsi</option>
            {[...new Set(lokasi.map((l) => l.provinsi))].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <select
            value={selectedKab}
            onChange={(e) => setSelectedKab(e.target.value)}
          >
            <option value="">Kabupaten</option>
            {[...new Set(
              lokasi
                .filter((l) => !selectedProv || l.provinsi === selectedProv)
                .map((l) => l.kabupaten)
            )].map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>

          <select
            value={selectedKec}
            onChange={(e) => setSelectedKec(e.target.value)}
          >
            <option value="">Kecamatan</option>
            {[...new Set(
              lokasi
                .filter(
                  (l) =>
                    (!selectedProv || l.provinsi === selectedProv) &&
                    (!selectedKab || l.kabupaten === selectedKab)
                )
                .map((l) => l.kecamatan)
            )].map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>

          <button className="as-btn primary" onClick={handleFilter}>
            Filter
          </button>
          <button className="as-btn ghost" onClick={handleClear} title="Reset">
            <FaRedo />
          </button>
        </div>
      </div>

      <div className="as-main-layout">
        <div className="as-map-col">
          <MapContainer center={mapCenter} zoom={mapZoom} className="map">
        <MapController
          center={flyTo?.center}
          zoom={flyTo?.zoom}
          bounds={fitBounds}
        />
        <MapViewportSync onBoundsChange={setMapBounds} />
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {onlyValid(filtered).map((l) => (
          <Marker
            key={l.id}
            position={[l.lat, l.lng]}
            eventHandlers={{
              mouseover: () => {
                if (!openId) setHoveredId(l.id);
              },
              mouseout: () => {
                if (!openId) setHoveredId(null);
              },
              click: async () => {
                setOpenId(l.id);
                setHoveredId(null);
                await loadServices(l.id);
              },
            }}
          >
            {(hoveredId === l.id || openId === l.id) && (
              <Popup
                closeButton={false}
                autoPan={true}
                autoPanPadding={[20, 20]}
                autoPanPaddingTopLeft={[28, 80]}
                autoPanPaddingBottomRight={[28, 28]}
                className="custom-popup"
                offset={[0, -20]}
              >
                <button className="popup-close" onClick={() => setOpenId(null)}>
                  <FaTimes />
                </button>

                <div className="as-card">
                  <div className="as-head">
                    <div className="as-avatar as-avatar--initials">
                      {initialsOf(l.name)}
                    </div>
                    <div className="as-head-meta">
                      <div className="as-name">{l.name}</div>
                      <div className="as-addr">
                        {l.kecamatan}, {l.kabupaten}
                      </div>
                      {l.phone && (
                        <a
                          href={`https://wa.me/${toIndonesiaMsisdn(l.phone)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="as-phone"
                        >
                          {toDisplayPhone(l.phone)}
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="as-items">
                    {(servicesMap[l.id] || []).map((s, idx) => (
                      <div className="as-item" key={`${s.id || s.title}-${idx}`}>
                        <div className="as-item-thumb">
                          <ImageWithFallback src={s.photo_url} alt={serviceDisplayName(s)} />
                        </div>

                        <div className="as-item-meta">
                          <div className="as-item-title">{serviceDisplayName(s)}</div>
                          <div className="as-item-price">
                            <span className="price-label">Harga</span>
                            {formatRupiah(s.unit_price)}
                          </div>
                        </div>

                        <div className="as-item-actions left-under">
                          <button
                            type="button"
                            className="btn btn-light sm"
                            onClick={() => copyProductLink(l, s, idx)}
                            title="Copy link produk"
                          >
                            {copiedServiceKey === `${l.id}-${getServiceKey(s, idx)}`
                              ? "Copied"
                              : "Copy link"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-order sm"
                            onClick={() => openOrder(l, s, idx)}
                            title="Order"
                          >
                            Order
                          </button>
                        </div>
                      </div>
                    ))}

                    {servicesMap[l.id] && servicesMap[l.id].length === 0 && (
                      <div className="as-item">
                        <div className="as-item-thumb">
                          <span className="as-item-noimg">No Photo</span>
                        </div>
                        <div className="as-item-meta">
                          <div className="as-item-title">Belum ada layanan</div>
                          <div className="as-item-price">
                            <span className="price-label">Harga</span>—
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Popup>
            )}
          </Marker>
        ))}
          </MapContainer>
        </div>

        <aside className="as-info-col">
          <div className="as-info-panel">
            <h3>Informasi Sahabat Kakao</h3>

            <div className="as-info-services">
              {providersInViewSorted.length > 0 ? (
                providersInViewSorted.map((item) => (
                  <div className="as-info-provider" key={`inview-${item.id}`}>
                    <div className="as-provider-left">
                      <div className="as-avatar as-avatar--profile">
                        {item.profile_pic ? (
                          <ImageWithFallback
                            src={item.profile_pic}
                            alt={item.name}
                            fallbackClassName="as-avatar-fallback as-avatar--initials"
                            fallback={initialsOf(item.name)}
                          />
                        ) : (
                          <span className="as-avatar-fallback as-avatar--initials">
                            {initialsOf(item.name)}
                          </span>
                        )}
                      </div>
                      <div className="as-distance">
                        {Number.isFinite(item.distanceKm)
                          ? `${item.distanceKm.toFixed(1)} km`
                          : "-"}
                      </div>
                    </div>
                    <div className="as-head-meta">
                      <div className="as-name">{item.name}</div>
                      <div className="as-addr">
                        {item.kecamatan}, {item.kabupaten}
                      </div>
                      <div className="as-contact-title">Hubungi kami melalui</div>
                      <div className="as-contact-links">
                        {item.phone && (
                          <a
                            href={`https://wa.me/${toIndonesiaMsisdn(item.phone)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="as-contact-link wa"
                            title="WhatsApp"
                          >
                            <FaWhatsapp />
                          </a>
                        )}
                        {item.facebook_url && (
                          <a
                            href={item.facebook_url}
                            target="_blank"
                            rel="noreferrer"
                            className="as-contact-link fb"
                            title="Facebook"
                          >
                            <FaFacebookF />
                          </a>
                        )}
                        {item.instagram_url && (
                          <a
                            href={item.instagram_url}
                            target="_blank"
                            rel="noreferrer"
                            className="as-contact-link ig"
                            title="Instagram"
                          >
                            <FaInstagram />
                          </a>
                        )}
                        {item.tiktok_url && (
                          <a
                            href={item.tiktok_url}
                            target="_blank"
                            rel="noreferrer"
                            className="as-contact-link tk"
                            title="TikTok"
                          >
                            <FaTiktok />
                          </a>
                        )}
                      </div>
                      <button
                        type="button"
                        className="as-btn primary as-info-action"
                        onClick={() => openServicesModal(item)}
                      >
                        Lihat Layanan/Produk
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="as-empty">Belum ada data Sahabat Kakao pada area peta ini.</p>
              )}
            </div>
          </div>
        </aside>
      </div>

      <ServiceListModal
        open={servicesOpen}
        onClose={() => {
          setServicesOpen(false);
          setServiceModalProvider(null);
          setServiceModalServices([]);
        }}
        provider={serviceModalProvider || activeProvider}
        services={serviceModalServices.length ? serviceModalServices : activeServices}
        onCopyLink={copyProductLink}
        copiedServiceKey={copiedServiceKey}
        onOrder={async (service = null, serviceIndex = 0) => {
          setServicesOpen(false);
          if (serviceModalProvider) {
            await openOrder(serviceModalProvider, service, serviceIndex);
            return;
          }
          if (activeProvider) await openOrder(activeProvider, service, serviceIndex);
        }}
      />

      <OrderModal
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        provider={orderProvider}
        services={orderServices}
        currentUserId={currentUserId}
        selectedServiceId={orderServiceId}
      />
    </div>
  );
}
